<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="$t('button.add_new_m', { var: $t('attributes.payment') })"
    size="large"
  >
    <template #body>
      <div class="flex w-full flex-col gap-3">
        <div class="flex flex-row gap-x-3">
          <BaseButtonInput
            class="w-1/2"
            :label="$t('attributes.payment')"
            :placeholder="$t('attributes.payment')"
            name="amount"
            :value="payment.amount"
            :disabled="permission"
            :errors="errors.amount"
            :is-clickable="false"
            type="number"
            :min="0"
            :max="invoice.solde"
            icon="euro-sign"
            @change.self="getInputValue"
          />
          <div class="w-1/2">
            <BaseSimpleDatePicker
              :key="cEmissionDate"
              :label="$t('attributes.date')"
              :defaultValue="cEmissionDate"
              :disabled="permission"
              name="emission_date"
              :errors="errors.emission_date"
              @change="getInputValue"
            />
          </div>
        </div>

        <SelectCustomer
          :key="invoice.customer_id"
          :errors="errors"
          :get-input-value="getInputValue"
          :model="payment"
          :permission="permission"
          :metadata="metadata"
        />

        <BaseInput
          :label="$t('attributes.paid_by_names')"
          :placeholder="$t('attributes.paid_by_names')"
          name="purchaser"
          v-model="payment.purchaser"
          :disabled="permission"
          :errors="errors.purchaser"
        />

        <BaseSimpleRadioGroup
          name="payment_mode"
          :label="$t('attributes.payment_mode')"
          :defaultValue="null"
          :options="metadata.mode"
          :is-showing-key="true"
          :is-returning-key="true"
          :errors="errors.payment_mode"
          :disabled="permission"
          translationExtention="enums.invoicing.mode"
          @change.self="getInputValue"
        />

        <div
          v-if="payment.payment_mode === 'CHE'"
          class="my-3 grid grid-cols-2 gap-5"
        >
          <div>
            <BaseInput
              :label="$t('attributes.check_number')"
              :placeholder="$t('attributes.check_number')"
              name="check_number"
              v-model="payment.check_number"
              :required="true"
              :errors="errors.check_number"
              :disabled="permission"
            />
          </div>
          <div>
            <div class="col-span-2 xl:col-span-1">
              <BaseSelectSimple
                v-if="metadata && metadata.bank"
                :label="$t('attributes.bank_name')"
                :placeholder="$t('attributes.bank_name')"
                name="issuing_bank"
                :defaultValue="
                  metadata.bank?.find(
                    (bank) => bank.id === invoice?.customer?.default_bank_id
                  )
                "
                :options="metadata.bank"
                :disabled="permission"
                :required="true"
                :searchable="true"
                :errors="errors.issuing_bank"
                @setSelected.self="getInputValue"
              />
            </div>
          </div>
        </div>

        <!--            <SelectUserSimple-->
        <!--              :errors='errors'-->
        <!--              :get-input-value='getInputValue'-->
        <!--              :metadata='metadata'-->
        <!--              :model='payment'-->
        <!--              name='recipient_id'-->
        <!--              :label="$t('attributes.received_by')"-->
        <!--              :permission="permission" />-->
        <BaseShowEditFilteredUsersComponent
          :label="$t('attributes.received_by')"
          v-model="payment.recipient_id"
          :editionMode="true"
          name="recipient_id"
          :metadata="metadata"
          :permission="permission"
          :errors="errors?.recipient_id"
        />

        <BaseTextArea
          :label="$t('attributes.description')"
          :placeholder="$t('attributes.description')"
          name="comment"
          :value="payment.comment"
          :errors="errors.comment"
          :disabled="permission"
          @change.self="getInputValue"
        />

        <BaseSelectSimple
          :key="payment"
          v-if="payment.next_invoice_status && metadata.invoice_status"
          :label="$t('attributes.next_invoice_status')"
          :placeholder="$t('attributes.next_invoice_status')"
          name="next_invoice_status"
          :defaultValue="
            metadata.invoice_status?.filter(
              (el) => el.id === payment.next_invoice_status
            )
          "
          :options="metadata.invoice_status"
          :disabled="permission"
          :errors="errors.next_invoice_status"
          :need-translation="true"
          translationPrefix="enums.invoicing.status"
          @setSelected="getNextStatusValue"
        />
      </div>
    </template>
    <template #footer>
      <BaseButton
        :title="$t('button.save_new_m', { var: $t('attributes.payment') })"
        icon="plus"
        color="primary"
        @click="submitForm"
      />
      <BaseButton :title="$t('button.close')" color="dark" @click="close" />
    </template>
  </BaseCenteredModal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import moment from 'moment'
import BaseShowEditFilteredUsersComponent from '@c/GlobalFormComponents/BaseShowEditFilterdUsersComponent.vue'

export default {
  name: 'InvoicingAddPaimentDialog',
  components: { BaseCenteredModal, BaseShowEditFilteredUsersComponent },
  emits: ['close', 'backdropClicked'],
  props: {
    isModalVisible: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      next_invoice_status: null
    }
  },
  computed: {
    ...mapGetters({
      payment: 'invoicing/getPayment',
      metadata: 'invoicing/getPaymentMetadata',
      invoice: 'invoicing/getInvoice',
      errors: 'auth/getErrors'
    }),
    localIsModalOpen: {
      get() {
        return this.isModalVisible
      },
      set(value) {
        if (!value) this.close()
      }
    },
    cEmissionDate() {
      return this.payment.emission_date
        ? new Date(moment(this.payment.emission_date, 'DD/MM/YYYY'))
        : null
    },
    cCustomerDefaultValue() {
      return this.invoice.customer
        ? this.metadata.customers.filter(
            (el) => el.id === this.invoice.customer.id
          )
        : {}
    },
    permission() {
      return !this.$can('manage_invoicing')
    }
  },
  methods: {
    ...mapActions({
      fetchNewPayment: 'invoicing/fetchNewPayment',
      postNewPayment: 'invoicing/storeNewPayment'
    }),
    ...mapMutations({
      alterPayment: 'invoicing/alterPayment',
      setErrors: 'auth/setErrors'
    }),

    getInputValue({ attribute, value }) {
      if (attribute === 'payment_mode') {
        this.alterPayment({
          attribute: 'issuing_bank',
          value:
            value === 'CHE' ? this.invoice?.customer?.default_bank_id : null
        })
      }
      this.alterPayment({ attribute: attribute, value: value })
    },
    getNextStatusValue({ attribute, value }) {
      this.next_invoice_status = value
    },
    submitForm() {
      this.postNewPayment({
        invoice_id: this.$route.params.invoicing_id,
        data_request: { ...this.payment }
      }).then(() => {
        this.$h.toastSuccess(
          this.$t('toasts.created_m', { var: this.$t('global.payment') })
        )
        this.close()
      })
    },
    close() {
      this.$emit('close')
    }
  },
  watch: {
    payment() {
      if (this.payment?.customer_id === null && this.invoice?.customer?.id) {
        this.alterPayment({
          attribute: 'customer_id',
          value: this.invoice?.customer?.id
        })
      }
      this.alterPayment({
        attribute: 'next_invoice_status',
        value: this.invoice.status
      })
    },
    isModalVisible(currentValue, _prevValue) {
      if (currentValue != _prevValue && currentValue) {
        this.fetchNewPayment({ invoice_id: this.$route.params.invoicing_id })
        this.alterPayment({
          attribute: 'next_invoice_status',
          value: this.invoice.status
        })
      }
    }
  }
}
</script>
